// Polls over the reliable data channel (Meeting Tools). Like chat, the payload
// rides the E2EE data path in encrypted meetings — the SFU only ever relays
// ciphertext, so poll questions, options and votes are never visible to our
// infrastructure. Ephemeral by design: nothing here persists past the meeting.
//
// Host-authoritative model: the host is the tally coordinator. Attendees send a
// `vote` addressed only to the host; the host dedupes by the SFU-signed sender
// identity and broadcasts `state` carrying AGGREGATE counts only (and only when
// results are shared). No per-voter data ever leaves the host — soft anonymity.

export const POLL_TOPIC = 'bz.poll';

export interface PollOption {
  id: string;
  label: string;
}

export interface PollDef {
  id: string;
  title: string;
  options: PollOption[];
  /** Responses are not attributed to names in any UI or broadcast. */
  anonymous: boolean;
  /** Whether attendees see the running tally (host always sees it). */
  showResults: boolean;
  state: 'live' | 'closed';
}

export type PollWire =
  // Host → everyone: the authoritative poll. counts/total are present ONLY when
  // showResults is true, so a hidden tally never crosses the wire.
  | { t: 'state'; poll: PollDef; counts?: Record<string, number>; total?: number }
  // Attendee → host (targeted): a single vote. The host dedupes by sender.
  | { t: 'vote'; pollId: string; optionId: string }
  // Host → everyone: this poll is gone.
  | { t: 'remove'; pollId: string };

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function encodePoll(wire: PollWire): Uint8Array<ArrayBuffer> {
  return new Uint8Array(encoder.encode(JSON.stringify(wire)));
}

export function decodePoll(payload: Uint8Array): PollWire | null {
  try {
    const w = JSON.parse(decoder.decode(payload)) as PollWire;
    switch (w?.t) {
      case 'vote':
        return typeof w.pollId === 'string' && typeof w.optionId === 'string' ? w : null;
      case 'remove':
        return typeof w.pollId === 'string' ? w : null;
      case 'state': {
        const p = w.poll;
        if (!p || typeof p.id !== 'string' || typeof p.title !== 'string' || !Array.isArray(p.options))
          return null;
        // Bound defensively (rendering is escape-by-default, §9.2).
        p.title = p.title.slice(0, 300);
        p.options = p.options
          .slice(0, 10)
          .filter((o) => o && typeof o.id === 'string' && typeof o.label === 'string')
          .map((o) => ({ id: o.id, label: o.label.slice(0, 300) }));
        p.state = p.state === 'closed' ? 'closed' : 'live';
        p.anonymous = !!p.anonymous;
        p.showResults = !!p.showResults;
        return w;
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
}

export function randomPollId(): string {
  const b = new Uint8Array(8);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}
